import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Stack,
  Typography,
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  MoreVert,
  Delete,
  Download,
  Refresh,
  Edit,
  BugReport,
  Code,
  ChevronLeft,
  Visibility,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import DeleteInvoiceModal from "./DeleteInvoiceModal";
import ChangeStatusModal from "./ChangeStatusModal";
import RerenderInvoicePDFModal from "./RerenderInvoicePDFModal";
import TriggerEventModal from "../../debug/components/TriggerEventModal";
import JsonModal from "components/JsonModal";

function InvoicePanel({ invoice, actions }) {
  const navigate = useNavigate();
  const location = useLocation();
  const preferences = useSelector(
    (state) => state.preferenceReducer.preferences
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [changeStatusModalOpen, setChangeStatusModalOpen] = useState(false);
  const [rerenderModalOpen, setRerenderModalOpen] = useState(false);
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const open = Boolean(anchorEl);
  // Check if we're on the edit route
  const isEditRoute = location.pathname.includes("/edit");

  if (!invoice) {
    return null;
  }

  const { id, name, type } = invoice;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleViewClient = useCallback(() => {
    setAnchorEl(null);
    if (!invoice?.client_id) {
      return;
    }
    navigate(`/clients/${invoice.client_id}#invoices`);
  }, [invoice, navigate]);

  const handleDelete = useCallback(() => {
    setAnchorEl(null);
    setDeleteModalOpen(true);
  }, []);

  const handleRebuildPDF = useCallback(() => {
    setAnchorEl(null);
    setRerenderModalOpen(true);
  }, []);

  const handleTriggerEvent = useCallback(() => {
    setAnchorEl(null);
    setTriggerModalOpen(true);
  }, []);

  const handleShowJSON = useCallback(() => {
    setAnchorEl(null);
    setJsonModalOpen(true);
  }, []);

  const handleEdit = useCallback(() => {
    setAnchorEl(null);
    if (!id) {
      return;
    }
    navigate(`/engagements/${id}/edit`);
  }, [id, navigate]);

  return (
    <Card
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        mb: 2,
        minWidth: 220,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ flexGrow: 1, minWidth: 0 }}
      >
        {isEditRoute && (
          <IconButton
            size="small"
            aria-label="Back to invoice page"
            component="a"
            href={`/engagements/${id}`}
            sx={{ mr: 0.5 }}
          >
            <ChevronLeft />
          </IconButton>
        )}
        <Typography variant="h3" noWrap title={name || "Unnamed Invoice"}>
          {name || "Unnamed Invoice"}
        </Typography>

        {type ? (
          <Chip
            label={
              type === "hourly" ? "Hourly" :
              type === "project_price" ? "Project Price" :
              type === "retainer_monthly" ? "Monthly Retainer" :
              type === "retainer_rolling" ? "Rolling Retainer" :
              type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
            }
            size="small"
            color="primary"
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
        ) : null}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        {invoice.status && (
          <Chip
            label={invoice.status.toUpperCase()}
            size="small"
            variant="outlined"
            // color={invoice.status === "closed" ? "success" : "warning"}
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              fontSize: "0.7rem",
            }}
          />
        )}
        {invoice.invoice_cycle && (
          <Tooltip
            title={
              invoice.invoice_cycle === "weekly"
                ? "This invoice is set for a Weekly invoice cycle (Mon-Sun)."
                : invoice.invoice_cycle === "biweekly"
                ? "This invoice is set for a Biweekly invoice cycle (every 2 weeks)."
                : invoice.invoice_cycle === "monthly"
                ? "This invoice is set for a Monthly invoice cycle."
                : "No invoice cycle set"
            }
            arrow
          >
            <Chip
              label={(invoice.invoice_cycle || "none").toUpperCase()}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                fontSize: "0.7rem",
              }}
            />
          </Tooltip>
        )}
        {invoice.invoice_processing && (
          <Tooltip
            title={
              invoice.invoice_processing === "auto"
                ? "This invoice will be automatically sent to the client."
                : invoice.invoice_processing === "manual"
                ? "This invoice is held for review before sending."
                : "No invoice processing setting configured"
            }
            arrow
          >
            <Chip
              label={(invoice.invoice_processing || "none").toUpperCase()}
              size="small"
              // color="secondary"
              variant="outlined"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                fontSize: "0.7rem",
              }}
            />
          </Tooltip>
        )}
        {invoice.status === "closed" &&
          invoice.sent !== undefined &&
          invoice.paid !== undefined && (
            <Divider orientation="vertical" flexItem />
          )}
        {invoice.status === "closed" && invoice.sent !== undefined && (
          <Tooltip
            title={
              invoice.sent === "yes"
                ? "This invoice has been sent."
                : "This invoice has not been been sent."
            }
            arrow
          >
            <Chip
              label={invoice.sent === "yes" ? "Sent" : "Not Sent"}
              size="small"
              color={invoice.sent === "yes" ? "success" : "error"}
              variant="contained"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                // fontSize: "0.7rem",
              }}
            />
          </Tooltip>
        )}{" "}
        {invoice.status === "closed" && invoice.paid !== undefined && (
          <Tooltip
            title={
              invoice.paid === "yes"
                ? "This invoice has been paid."
                : "This invoice has not been paid."
            }
            arrow
          >
            <Chip
              label={invoice.paid === "yes" ? "Paid" : "Unpaid"}
              size="small"
              color={invoice.paid === "yes" ? "success" : "error"}
              variant="contained"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                // fontSize: "0.7rem",
              }}
            />
          </Tooltip>
        )}
        <IconButton
          onClick={handleClick}
          size="small"
          aria-label="invoice actions"
          aria-controls={open ? "invoice-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <MoreVert />
        </IconButton>
        <Menu
          id="invoice-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            menuList: {
              "aria-labelledby": "invoice-actions-button",
            },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleViewClient} disabled={!id}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Client</ListItemText>
          </MenuItem>
          {!isEditRoute && (
            <>
              <MenuItem onClick={handleEdit} disabled={!id}>
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
              <Divider />
            </>
          )}
          {invoice.status === "closed" && invoice.pdf_url !== undefined && (
            <MenuItem onClick={() => window.open(invoice.pdf_url, "_blank")}>
              <ListItemIcon>
                <Download fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download PDF</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleRebuildPDF} disabled={!id}>
            <ListItemIcon>
              <Refresh fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rebuild PDF</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDelete} disabled={!id}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Invoice</ListItemText>
          </MenuItem>
          {preferences?.debugMode === 1 && (
            <>
              <Divider />
              <MenuItem onClick={handleShowJSON} disabled={!id}>
                <ListItemIcon>
                  <Code fontSize="small" />
                </ListItemIcon>
                <ListItemText>Show JSON</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleTriggerEvent} disabled={!id}>
                <ListItemIcon>
                  <BugReport fontSize="small" />
                </ListItemIcon>
                <ListItemText>Trigger Event</ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>
        {actions ? <Box>{actions}</Box> : null}
      </Stack>
      <DeleteInvoiceModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        invoice={invoice}
      />
      <ChangeStatusModal
        open={changeStatusModalOpen}
        onClose={() => setChangeStatusModalOpen(false)}
        invoice={invoice}
      />
      <RerenderInvoicePDFModal
        open={rerenderModalOpen}
        onClose={() => setRerenderModalOpen(false)}
        invoice={invoice}
      />
      {preferences?.debugMode === 1 && (
        <>
          <TriggerEventModal
            open={triggerModalOpen}
            onClose={() => setTriggerModalOpen(false)}
            initialData={id ? { invoiceId: id } : null}
            filterPrefix="invoice"
          />
          <JsonModal
            open={jsonModalOpen}
            onClose={() => setJsonModalOpen(false)}
            data={invoice}
            title="Invoice JSON"
          />
        </>
      )}
    </Card>
  );
}

InvoicePanel.propTypes = {
  client: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    type: PropTypes.string,
  }),
  actions: PropTypes.node,
};

export default InvoicePanel;
